import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scrollToHash, isMobileOrTablet } from '../utils';

// 記述が多いのもはimport
import { registerBreakPointEvent } from './registerBreakPointEvent';
gsap.registerPlugin(ScrollTrigger);

/**
 * @function rewriteTellink
 * @description PCの場合、<a href="tel:">を<span>に変更、class等attributeはそのまま継承
 * */
const _rewriteAtag = (el: HTMLElement): void => {
  const attributes = el.attributes;
  const innerHtml = el.innerHTML;
  let attributesText = null;
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (attribute.name != 'href') {
      const t = `${attribute.name}="${attribute.textContent}"`;
      attributesText = attributesText == null ? t : `${t} ${attributesText}`;
    }
  }
  if (attributesText == null) {
    el.outerHTML = `<span>${innerHtml}</span>`;
  } else {
    el.outerHTML = `<span ${attributesText}>${innerHtml}</span>`;
  }
};

const rewriteTellink = () => {
  const tel_links = document.querySelectorAll('a[href^="tel:"]') as NodeListOf<HTMLElement>;
  if (tel_links.length && !isMobileOrTablet()) {
    tel_links.forEach((tel_link) => {
      _rewriteAtag(tel_link);
    });
  }
};

/**
 * @function smoothScroll
 * @description ページ内リンクのスムーススクロール
 * */
const _getSamePageAnchor = (url: URL): string => {
  if (url.protocol !== window.location.protocol || url.host !== window.location.host || url.pathname !== window.location.pathname || url.search !== window.location.search) {
    return '';
  }
  return url.hash;
};
const smoothScroll = (): void => {
  const anchor_links = document.querySelectorAll('a[href^="#"]') as NodeListOf<HTMLElement>;
  if (anchor_links.length) {
    anchor_links.forEach((anchor_link) => {
      anchor_link.addEventListener('click', (e) => {
        const hash = anchor_link.getAttribute('href');
        if (hash) {
          scrollToHash(hash, e);
        }
      });
    });
  }
  if (location.hash) {
    scrollToHash(location.hash, null);
  }
};

/**
 * @function adjustViewport
 * @description viewport 375px以下は width=375 で固定にする
 * */
const adjustViewport = (): void => {
  const triggerWidth = 375;
  const viewport = document.querySelector('meta[name="viewport"]');
  const value = window.outerWidth < triggerWidth ? `width=${triggerWidth}, target-densitydpi=device-dpi` : 'width=device-width, initial-scale=1,maximum-scale=1.0';
  if (viewport) {
    viewport.setAttribute('content', value);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = value;
    document.head.appendChild(meta);
  }
};
/**
 * @function setRootProperty
 * @description :root{  }に動的なcss変数を設定
 * */
const setRootProperty = (): void => {
  const root = document.querySelector(':root') as HTMLElement;
  if (root) {
    root.style.setProperty('--view-width', document.body.clientWidth + 'px');
  }
};

/**
 * @function parallaxImage
 * */
const parallaxImage = (): void => {
  const images = document.querySelectorAll('.c-parallax-image') as NodeListOf<HTMLElement>;
  if (images.length) {
    images.forEach((image) => {
      const rect = image.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom < window.innerHeight) {
        const child = image.children[0] as HTMLElement;
        if (child.clientHeight > rect.height) {
          if (image.classList.contains('is-sticky') && window.innerWidth > 768) {
            const group = image.closest('.c-menu-group') as HTMLElement;
            const groupRect = group.getBoundingClientRect();
            const diffY = child.clientHeight - rect.height;
            const viewLength = window.innerHeight + groupRect.height;
            const percentY = groupRect.bottom / viewLength - 1;
            const y = diffY * percentY;
            gsap.to(child, {
              y: `${y}px`,
            });
          } else {
            const diffY = child.clientHeight - rect.height;
            const viewLength = window.innerHeight + rect.height;
            const percentY = rect.bottom / viewLength - 1;
            const y = diffY * percentY;
            gsap.to(child, {
              y: `${y}px`,
            });
          }
        }
      }
    });
  }
};

/**
 * @function faqAcdn
 * */
const faqAcdn = (): void => {
  const groups = document.querySelectorAll('.c-faq-group') as NodeListOf<HTMLElement>;
  if (groups.length) {
    groups.forEach((group) => {
      const button = group.querySelector('.c-faq-group__q') as HTMLButtonElement;
      const content = group.querySelector('.c-faq-group__a') as HTMLElement;
      button.addEventListener('click', () => {
        button.classList.toggle('is-open');
        if (button.classList.contains('is-open')) {
          gsap.to(content, {
            height: 'auto',
          });
        } else {
          gsap.to(content, {
            height: 0,
          });
        }
      });
    });
  }
};

/**
 * @function toggleHeader
 * */
const toggleHeader = () => {
  const header = document.querySelector('.l-header') as HTMLElement;
  if (window.scrollY < window.innerHeight) {
    header.classList.remove('is-scrolled');
  } else {
    header.classList.add('is-scrolled');
  }
};

/**
 * @function fadeAnime
 * */

const fadeAnime = () => {
  const els = document.querySelectorAll('.js-fade') as NodeListOf<HTMLElement>;
  els.forEach((el) => {
    gsap.set(el, {
      y: 20,
      autoAlpha: 0,
    });
    gsap.to(el, {
      y: 0,
      autoAlpha: 1,
      duration: 0.8,
      scrollTrigger: {
        trigger: el,
        toggleActions: 'play none none none',
        start: 'top+=200 bottom',
        markers: false,
      },
    });
  });
};

/**
 * @function navbtnHover
 * */
const navbtnHover = (): void => {
  const btns = document.querySelectorAll('.c-nav-btn') as NodeListOf<HTMLElement>;
  if (btns.length) {
    btns.forEach((btn) => {
      const boldTag = btn.querySelector('b') as HTMLElement;
      const text = boldTag.textContent || '';
      boldTag.innerHTML = [...text].map((char) => `<i>${char}</i>`).join('');
      btn.addEventListener('mouseenter', () => {
        const itags = btn.querySelectorAll('i') as NodeListOf<HTMLElement>;
        gsap.to(itags, {
          alpha: 0.4,
          duration: 0.1,
          stagger: {
            amount: 0.3,
            from: 'random',
          },
          onComplete: () => {
            itags.forEach((i) => {
              i.style.opacity = '0.4';
            });
          },
        });
      });
      btn.addEventListener('mouseleave', () => {
        const itags = btn.querySelectorAll('i') as NodeListOf<HTMLElement>;
        gsap.to(itags, {
          alpha: 1,
          duration: 0.1,
          stagger: {
            amount: 0.3,
            from: 'random',
          },
          onComplete: () => {
            itags.forEach((i) => {
              i.style.opacity = '1';
            });
          },
        });
      });
    });
  }
};

const cmenuGroup = () => {
  const groups = document.querySelectorAll('.c-menu-group') as NodeListOf<HTMLElement>;
  if (groups.length) {
    groups.forEach((group) => {
      const image = group.querySelector('.c-menu-group__image') as HTMLElement;
      if (!image) return;
      const rect = group.getBoundingClientRect();
      if (rect.bottom <= image.clientHeight) {
        group.classList.add('is-end');
        group.classList.add('is-fixed');
      } else if (rect.top <= 0) {
        group.classList.remove('is-end');
        group.classList.add('is-fixed');
      } else {
        group.classList.remove('is-end');
        group.classList.remove('is-fixed');
      }
    });
  }
};

// export object
const Common = {
  init() {
    registerBreakPointEvent();
    adjustViewport();
    setRootProperty();
    rewriteTellink();
    smoothScroll();
    faqAcdn();
    fadeAnime();
    toggleHeader();
    navbtnHover();
    cmenuGroup();
  },
  resize() {
    adjustViewport();
    setRootProperty();
  },
  breakPoint() {},
  scroll() {
    parallaxImage();
    cmenuGroup();
    toggleHeader();
  },
};

export { Common };
